import { useMutation } from '@apollo/client'
import { jwtDecode } from 'jwt-decode'
import { Loader } from 'lucide-react'
import { use, useEffect, useTransition } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { AuthContext, AuthDispatchContext } from '@/components/auth/context'
import { ToasterContent } from '@/components/sv/toaster-content'
import { gql } from '@/lib/graphql/gql'
import type {
  ValidateVerificationTokenMutation,
  ValidateVerificationTokenMutationVariables,
} from '@/routes/regtoken/index.types.gen'

const VALIDATE_TOKEN_MUTATION = gql`
  mutation validateVerificationToken($verificationEmailToken: String!) {
    validateVerificationToken(verificationEmailToken: $verificationEmailToken)
  }
`

export const Regtoken = () => {
  const navigate = useNavigate()
  const [isPending, startTransition] = useTransition()
  const [searchParams] = useSearchParams()
  const idCompany = searchParams.get('idCompany') || ''
  const token = searchParams.get('loginToken') || ''
  const emailVerificationToken =
    searchParams.get('emailVerificationToken') || ''
  const { login } = use(AuthDispatchContext) ?? {}
  const { isAuthenticated } = use(AuthContext) ?? {}

  const [validateEmail] = useMutation<
    ValidateVerificationTokenMutation,
    ValidateVerificationTokenMutationVariables
  >(VALIDATE_TOKEN_MUTATION)

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode<{ samlExp?: number }>(token)
        // Honor an inbound loginToken only when the user isn't authenticated
        // (fresh login) or when it carries SAML re-auth claims that refresh
        // the active session — otherwise (e.g. email-verification links with
        // a short-lived companion token) leave the existing session alone.
        if (!isAuthenticated || decoded.samlExp != null) {
          login?.(token)
        }
      } catch {
        navigate('/login')
        return
      }
      if (!emailVerificationToken) {
        navigate(`/${idCompany ? `?idCompany=${idCompany}` : ''}`)
      }
      return
    }

    if (isAuthenticated) {
      navigate(`/${idCompany ? `?idCompany=${idCompany}` : ''}`)
    }
  }, [])

  useEffect(() => {
    if (isPending) {
      return
    }

    if (!isAuthenticated) {
      try {
        jwtDecode(token)
        login?.(token)
        if (!emailVerificationToken) {
          navigate(`/${idCompany ? `?idCompany=${idCompany}` : ''}`)
        }
      } catch {
        navigate('/login')
      }
      return
    }

    startTransition(async () => {
      if (emailVerificationToken) {
        try {
          const res = await validateEmail({
            variables: { verificationEmailToken: emailVerificationToken },
          })
          if (
            res?.data?.validateVerificationToken === 'ALREADY_VERIFIED' ||
            res?.data?.validateVerificationToken === 'SUCCESS'
          ) {
            toast(
              <ToasterContent variant="success">
                Email address verified successfully
              </ToasterContent>,
              { position: 'top-right' }
            )
          } else {
            throw new Error(`Failed to verify email address`)
          }
        } catch {
          toast(
            <ToasterContent variant="error">
              Failed to verify email address
            </ToasterContent>,
            { position: 'top-right' }
          )
        }
        navigate('/')
      }
    })
  }, [isAuthenticated])

  return isPending ? (
    <div className="flex items-center gap-2 w-full justify-center p-20">
      <Loader className="animate-spin" /> Validating email address
    </div>
  ) : null
}
